/* Schema-Owner für den Spielerdatensatz.
 *
 * ACHTUNG — die wichtigste Datei im Datenmodell.
 *
 * Postgres speichert einen Spieler als eine Zeile mit einem einzigen
 * JSONB-Feld und validiert dessen Inhalt NICHT. Diese Datei ist damit die
 * einzige Stelle, die festlegt, welche Felder ein Spieler hat.
 *
 * Daraus folgt:
 *   - Ein neues Feld existiert erst, wenn es hier mit Vorgabewert steht.
 *   - Es gibt keine Datenmigration. Bestandsdatensätze bekommen ein neues
 *     Feld erst beim nächsten Speichern — jeder Lesecode muss den Vorgabewert
 *     vertragen.
 *   - Ein Feld umbenennen heißt Datenverlust, wenn hier kein Rückfall auf den
 *     alten Namen steht: neu = neu ?? alt ?? vorgabe.
 *
 * Unterschied zur alten Fassung in index.html: Diese Funktion verändert das
 * übergebene Objekt NICHT, sondern liefert ein neues. Das ist für Vues
 * Reaktivität nötig und verhindert, dass geladene Rohdaten unbemerkt mutiert
 * werden.
 */

import { CONTRACT_MEMORY_KEYS } from './contracts.js';
import { POSITION_TBD } from './positions.js';
import { normalizeTraits } from './playerKeywords.js';
import { normalizeAgeGroup } from './ageGroups.js';

const EMPTY_KEY_STAT = { label: '', value: '', pct: 0 };

function str(v) {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}
function arr(v) {
  return Array.isArray(v) ? v : [];
}
function obj(v) {
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
}

export function ensurePlayerShape(raw) {
  const p = obj(raw);

  return {
    ...p,

    id: p.id,
    _isNew: p._isNew === true,

    // --- Stammdaten ---
    firstName: str(p.firstName),
    lastName: str(p.lastName),
    status: p.status || 'trial',
    pos: p.pos || POSITION_TBD,
    pos2: str(p.pos2),
    foot: str(p.foot),
    jg: p.jg || '',
    height: str(p.height),
    /* Altersklasse (domain/ageGroups.js): "U17", "U19" oder leer; Unbekanntes wird leer. Staff liest sie über
       die Ansicht players_staff (Positivliste). */
    ageGroup: normalizeAgeGroup(p.ageGroup),
    jerseyNumber: p.jerseyNumber ?? '',
    currentClub: str(p.currentClub),
    /* Logo des aufnehmenden Vereins (Pfad im Speicher). Übergangsregel Klubregister: Text und Logo-Pfad
       bleiben gesetzt, auch wenn ein Verweis da ist (domain/footballClub.js stampClub). */
    currentClubLogoPath: p.currentClubLogoPath || null,
    /* Verweis auf den Verein im Register (Spalte players.current_club_id, NICHT im Datenblock: Die Spalte
       entsteht mit supabase/klubregister-4-spalten.sql; stripTransient nimmt das Feld vor dem Schreiben des
       Datenblocks heraus, supabase/players.js schreibt es in die Spalte). Ohne Verweis: null. */
    currentClubId: p.currentClubId || null,

    // --- Identität ---
    dob: str(p.dob),
    pob: str(p.pob),
    nationality: str(p.nationality),
    nationalTeam: str(p.nationalTeam),
    ntLevel: str(p.ntLevel),
    passportNumber: str(p.passportNumber),
    passportExpiry: str(p.passportExpiry),

    // --- Zeitpunkte ---
    addedDate: str(p.addedDate),
    atGrassrunnersSince: str(p.atGrassrunnersSince),
    releasedDate: str(p.releasedDate),

    // --- Bilder und Dateien ---
    avatar: p.avatar || null,
    avatarPath: p.avatarPath || null,
    bioPhoto: p.bioPhoto || null,
    bioPhotoPath: p.bioPhotoPath || null,
    /* Die alte hochgeladene Spielerkarte (playerCardPdf) liest und schreibt die App seit F5 nicht mehr. Ein gespeicherter Wert
       bleibt im Datensatz unangetastet: der Spread oben gibt ihn beim Speichern unverändert zurück. */
    /* Genau ein Highlight-Video, wie die Spielerkarte ein einzelner Platz —
       keine Liste. Beliebig viele Match-Auftritte stehen weiterhin in
       videos[] weiter unten. */
    highlightVideo: p.highlightVideo || null,

    // --- Profil ---
    tags: arr(p.tags),
    facts: arr(p.facts),
    keyStat: { ...EMPTY_KEY_STAT, ...obj(p.keyStat) },
    transfermarktUrl: str(p.transfermarktUrl),

    // --- Status und Dokumente ---
    readyToTravel: obj(p.readyToTravel),
    docs: obj(p.docs),
    identityDocs: obj(p.identityDocs),
    generalDocs: arr(p.generalDocs),
    federationDocs: arr(p.federationDocs),

    // --- Kaufmännisch: seit Stufe 3 (Teil 2) NICHT mehr hier ---
    // transferInfo und clubContracts stehen in player_contracts und
    // player_contract_files (domain/contracts.js). Die Datenbank lehnt gefüllte
    // Werte im Datenblock ab. Hier bewusst kein Vorgabewert: Ein Altstand, der
    // sie noch enthält, wird nicht verändert (der Spread oben lässt ihn
    // durch), aber sie werden nicht mehr neu angelegt.

    // --- Sonstiges ---
    trialReports: arr(p.trialReports),
    videos: arr(p.videos),
    /* Schlagwörter der Player Card: höchstens drei, Schlüssel einer festen Liste (domain/playerKeywords.js); Altwerte
       (freier Text) werden auf den Schlüssel abgebildet, Unbekanntes bleibt erhalten. Liegt im Datenblock. */
    traits: normalizeTraits(p.traits),
    invitations: arr(p.invitations),
    visas: arr(p.visas),
    feed: arr(p.feed),
    trips: arr(p.trips).map((t) => ({ ...obj(t), updates: arr(t && t.updates) })),
  };
}

/* Felder, die nur der Oberfläche dienen und nicht gespeichert werden.
   contract, contractFiles und removedContractFiles sind die Vertragsdaten im
   Speicher (domain/contracts.js) — sie gehen in eigene Tabellen, nie in den
   Datenblock. currentClubId geht in die Spalte players.current_club_id (supabase/players.js). */
/* _staffView, passportComplete, passportDaysLeft: Objekt aus der Staff-Ansicht (supabase/players_staff). Die beiden
   abgeleiteten Pass-Werte stehen nie im Datenblock. */
const TRANSIENT_KEYS = [
  '_isNew', '_edit', '_publicOnly', '_staffView', 'passportComplete', 'passportDaysLeft', 'currentClubId',
  ...CONTRACT_MEMORY_KEYS,
];

/** Datensatz für das Speichern vorbereiten: Oberflächen-Flags entfernen. */
export function stripTransient(player) {
  const clean = JSON.parse(JSON.stringify(player));
  TRANSIENT_KEYS.forEach((k) => delete clean[k]);
  /* Leere Altschlüssel nicht zurückschreiben (die Sperre der Datenbank würde
     sie ohnehin entfernen). Gefüllte bleiben stehen: sie sind Altbestand, den
     die Datenbank beim Speichern ablehnt, statt ihn still zu verlieren. */
  if (clean.transferInfo && !Object.keys(clean.transferInfo).length) delete clean.transferInfo;
  if (Array.isArray(clean.clubContracts) && !clean.clubContracts.length) delete clean.clubContracts;
  (clean.visas || []).forEach((v) => TRANSIENT_KEYS.forEach((k) => delete v[k]));
  (clean.trips || []).forEach((t) => TRANSIENT_KEYS.forEach((k) => delete t[k]));
  (clean.invitations || []).forEach((v) => TRANSIENT_KEYS.forEach((k) => delete v[k]));
  return clean;
}
